import {
  BankOutlined,
  GithubOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useIntl, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.nickname || currentUser?.username}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const loginOut = async () => {
    localStorage.removeItem('user');
    history.push('/login');
  };
  const { styles } = useStyles();
  const intl = useIntl();

  const { initialState, setInitialState } = useModel('@@initialState');

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'profile') {
      history.push('/console/settings');
      return;
    }
    if (key === 'developerGithub') {
      history.push('/console/developer/github');
      return;
    }
    if (key === 'developerEnterprise') {
      history.push('/console/developer/enterprise');
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || (!currentUser.nickname && !currentUser.username)) {
    return loading;
  }

  const githubVerified = !!currentUser.githubVerified;
  const enterpriseCertified = !!currentUser.enterpriseCertified || !!currentUser.supplierCode;

  const menuItems = [
    ...(menu
      ? [
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: intl.formatMessage({ id: 'menu.account.profile', defaultMessage: '个人信息' }),
    },
    {
      key: 'developerGithub',
      icon: <GithubOutlined />,
      label: githubVerified
        ? intl.formatMessage({
            id: 'menu.account.developerGithubDone',
            defaultMessage: '开发者认证（已认证）',
          })
        : intl.formatMessage({
            id: 'menu.account.developerGithub',
            defaultMessage: '开发者认证',
          }),
    },
    {
      key: 'developerEnterprise',
      icon: <BankOutlined />,
      label: enterpriseCertified
        ? intl.formatMessage({
            id: 'menu.account.developerEnterpriseDone',
            defaultMessage: '企业认证（已认证）',
          })
        : intl.formatMessage({
            id: 'menu.account.developerEnterprise',
            defaultMessage: '企业认证',
          }),
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: intl.formatMessage({ id: 'menu.account.logout', defaultMessage: '退出登录' }),
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
